export interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined | null>
}

export interface RequestClientConfig {
  defaultApiBaseUrl: string
  defaultAdminBaseUrl?: string
  tokenKey?: string
  loginPath?: string
  namespace?: string
}

export function createPathNormalizer(namespace = 'admin') {
  return function normalizePath(url: string): string {
    if (!url || typeof url !== 'string') return url
    if (url.startsWith('http')) return url
    let p = url
    while (true) {
      const before = p
      const dupSlash = `/${namespace}/${namespace}/`
      const dupTail = `/${namespace}/${namespace}`
      if (p.startsWith(dupSlash)) {
        p = `/${namespace}/` + p.slice(dupSlash.length)
      } else if (p === dupTail) {
        p = `/${namespace}`
      }
      if (p === before) break
    }
    const nsPrefix = `/${namespace}/`
    const nsPrefix2 = `/${namespace}`
    if (p === nsPrefix2) {
      p = ''
    } else if (p.startsWith(nsPrefix)) {
      p = '/' + p.slice(nsPrefix.length)
    }
    return p
  }
}

export function createRequestClient(config: RequestClientConfig) {
  const tokenKey = config.tokenKey || 'admin_token'
  const loginPath = config.loginPath || '/login'
  const apiBaseUrl = config.defaultApiBaseUrl
  const adminBaseUrl = config.defaultAdminBaseUrl || `${apiBaseUrl}/admin`
  const normalizePath = createPathNormalizer(config.namespace || 'admin')

  const request = async <T = any>(url: string, options: RequestOptions = {}): Promise<T> => {
    const token = typeof window !== 'undefined' ? localStorage.getItem(tokenKey) : null

    const headers: Record<string, string> = {
      ...((options.headers as Record<string, string>) || {}),
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }

    if (!headers['Content-Type'] && !(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json'
    }

    const cleanUrl = normalizePath(url)
    const baseUrl = cleanUrl.startsWith('/auth') ? apiBaseUrl : adminBaseUrl

    let finalUrl = `${baseUrl}${cleanUrl}`

    if (options.params) {
      const searchParams = new URLSearchParams()
      Object.entries(options.params).forEach(([key, val]) => {
        if (val !== undefined && val !== null && val !== '') {
          searchParams.append(key, String(val))
        }
      })
      const queryString = searchParams.toString()
      if (queryString) {
        finalUrl += (finalUrl.includes('?') ? '&' : '?') + queryString
      }
    }

    const response = await fetch(finalUrl, {
      ...options,
      headers,
      credentials: 'include',
    })

    if (!response.ok) {
      if (response.status === 401) {
        if (window.location.pathname !== loginPath) {
          const current = window.location.pathname + window.location.search
          window.location.href = `${loginPath}?redirect=${encodeURIComponent(current)}`
        }
        throw new Error('登录凭证已过期或无效，请重新登录')
      }
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || error.error || `请求异常 (${response.status})`)
    }

    const json = await response.json()
    if (json && typeof json.code !== 'undefined') {
      if (json.code !== 0) {
        throw new Error(json.message || '操作失败')
      }
      return json.data as T
    }
    return json as T
  }

  return {
    request,
    normalizePath,
    apiBaseUrl,
    adminBaseUrl,
    tokenKey,
  }
}

export default createRequestClient
